import { ApiError } from '@/lib/apiClient';

/**
 * SLICE 52 — the login-phone error vocabulary, shared by every surface that can
 * now reject a phone number as a login.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 ONE PHONE, ONE IDENTITY — SO ONE VOCABULARY, READ FROM THE BODY.
 * ─────────────────────────────────────────────────────────────────────────────
 * "One phone, one identity across staff and tenants" (Mahad, 16 Sep) means the
 * SAME four refusals reach three different forms: editing a tenant user's login
 * (`PATCH /users/:id/phone`), creating a tenant (`POST /tenants`), and creating a
 * staff member (`POST /staff`). The server answers each with `{ error: '<code>' }`
 * and NO message — a unique-violation detail carries the conflicting phone number,
 * so the sentence has to be built on this side, mapped from the closed set below.
 *
 * The status code alone is NOT enough: a 400 on tenant-create can be a bad name
 * as easily as a bad phone, and a 409 on staff-create can be a duplicate staff
 * phone (`phone_is_staff`) as easily as a clash with a tenant user (`phone_in_use`)
 * or a SIM (`phone_is_sim`). So this reads the BODY's `error` string and returns a
 * phone code only when the server named one — the caller decides what to do with
 * `null` (fall back to its own generic).
 */

/** The four phone refusals the server can name. `not_found` (a 404 on the PATCH
 *  target) is handled by the edit dialog directly, not here — it is not a phone
 *  clash and no other surface wants it. */
export type LoginPhoneCode = 'phone_invalid' | 'phone_is_sim' | 'phone_in_use' | 'phone_is_staff';

const LOGIN_PHONE_CODES: readonly LoginPhoneCode[] = [
  'phone_invalid',
  'phone_is_sim',
  'phone_in_use',
  'phone_is_staff',
];

/** The raw `error` string on an ApiError body, or null when there is none. */
export function apiErrorCode(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  const body = err.body;
  if (body !== null && typeof body === 'object') {
    const code = (body as { error?: unknown }).error;
    if (typeof code === 'string') return code;
  }
  return null;
}

/**
 * The phone code the server NAMED, or null. Null means "not a phone refusal I
 * recognise" — never a status-based guess, because the neighbouring 400s and 409s
 * on these routes are not all about phones.
 */
export function loginPhoneCode(err: unknown): LoginPhoneCode | null {
  const code = apiErrorCode(err);
  return code !== null && (LOGIN_PHONE_CODES as readonly string[]).includes(code)
    ? (code as LoginPhoneCode)
    : null;
}
